import {
  Component,
  forwardRef,
  Injectable,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ValidatorFn,
} from "@angular/forms";
import { Subscription } from "rxjs";
import { NgbTimeStruct, NgbTimeAdapter } from "@ng-bootstrap/ng-bootstrap";
import { GenerateRandom } from "src/utils/random";
import { LabelMode } from "src/utils/label-mode.enum";

const pad = (i: number): string => (i < 10 ? `0${i}` : `${i}`);

@Injectable()
export class NgbTimeStringAdapter extends NgbTimeAdapter<string> {
  fromModel(value: string | null): NgbTimeStruct | null {
    if (!value) {
      return null;
    }
    const split = value.split(":");
    return {
      hour: parseInt(split[0], 10),
      minute: parseInt(split[1], 10),
      second: parseInt(split[2], 10),
    };
  }

  toModel(time: NgbTimeStruct | null): string | null {
    return time != null
      ? `${pad(time.hour)}:${pad(time.minute)}:${pad(time.second)}`
      : null;
  }
}

export function maxTimeValidator(maxHour: number, maxMinute: number): ValidatorFn {
  return (control: AbstractControl) => {
    const value = control.value;
    if (!value) {
      return null;
    }

    const [hour, minute] = value.split(":").map(Number);
    if (hour > maxHour || (hour === maxHour && minute > maxMinute)) {
      return { maxTime: true };
    }

    return null;
  }
}

@Component({
  selector: "app-timepicker",
  templateUrl: "./timepicker.component.html",
  styleUrls: ["./timepicker.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimepickerComponent),
      multi: true,
    },
    { provide: NgbTimeAdapter, useClass: NgbTimeStringAdapter },
  ],
})
export class TimepickerComponent
  implements OnInit, OnDestroy, ControlValueAccessor {
  subscription: Subscription;
  errorMessage: string;

  @Input()
  set isDisabled(value: boolean) {
    if (value) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  @Input()
  set error(value: any) {
    this.isError = false;
    if (value) {
      this.isError = true;
      this.errorMessage = "VALIDATOR.REQUIRED";
    }
  }

  @Input() set labelMode(value: LabelMode) {
    this.mode = value;
  }

  @Input() name = GenerateRandom(6);
  @Input() label: string;
  @Input() spinners: boolean = false;
  @Input() classes: string;
  @Input() modeClass: string;
  @Input() required: boolean = false;

  LabelMode = LabelMode;
  mode: LabelMode = LabelMode.DEFAULT;

  form = new FormControl<string | null>(null, maxTimeValidator(23, 59));

  isError = false;

  onChange = (value: string | null) => { };
  onTouched = (value: string) => { };

  constructor() { }

  ngOnInit(): void {
    this.subscription = this.form.valueChanges.subscribe((value) => {
      this.onChange(value);
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  writeValue(obj: any): void {
    this.form.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void { }
}
